import { useEffect, useState } from "react";

import i18next from "i18next";
import { useSelector } from "react-redux";

import { normalizePrice } from "../../../utils";

import { getAdditionalOptions, selectAdditionalOptions } from "../../../protocols";

import { DotsLoadingComponent } from "../../ui";
import SeatMap from "./SeatMap";
import {
  Chip,
  Chips,
  CurrentChoice,
  ErrorText,
  Hint,
  Item,
  ItemControls,
  ItemInfo,
  OptionName,
  PassengerName,
  Quantity,
  QuantityButton,
  SeatSummary,
  SeatToggle,
  SegmentTitle,
  Title,
  ToggleButton,
  Wrapper,
} from "./style";

// Ключ выбранного варианта: опция + код варианта (у одной опции несколько вариантов, напр. пакетов eSIM).
// У услуг перевозчика (багаж, питание) выбор делается на конкретного пассажира — тогда в ключе ещё и его индекс.
const KEY_SEPARATOR = "|";
const keyOf = (optionId, code, passengerIndex = null) =>
  [optionId, code, passengerIndex === null || passengerIndex === undefined ? "" : passengerIndex].join(KEY_SEPARATOR);

const parseKey = key => {
  const [optionId, code, passengerIndex] = key.split(KEY_SEPARATOR);
  return { optionId: Number(optionId), code, passengerIndex: passengerIndex === "" ? null : Number(passengerIndex) };
};

// Услуга перевозчика: продаётся конкретным пассажирам на конкретное плечо (passenger_indexes + segment_ref)
const isPassengerItem = item => Array.isArray(item.passenger_indexes);
// Место в салоне — тоже услуга перевозчика, но выбирается на схеме салона, а не из списка
const isSeatItem = item => item.kind === "seat";

// Код блюда/услуги без хвоста «@сегмент»: "AVML@191018" → "AVML"
const baseCode = code => String(code || "").split("@")[0];

// current_codes у питания: что у пассажира уже есть в брони (включено в тариф).
// Бек отдаёт объект {"1": "MLIN"} или массив ["MLIN"] (индекс = пассажир) — приводим к одному виду.
const currentCodeOf = (item, passengerIndex) => {
  const codes = item.current_codes;
  if (!codes || typeof codes !== "object") return null;
  const code = codes[passengerIndex] ?? codes[String(passengerIndex)];
  return code ? String(code) : null;
};

const itemName = (name, language) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name[language] || name.en || name.ru || Object.values(name)[0] || "";
};

/**
 * Дополнительные услуги к заказу (eSIM, багаж…) на странице оплаты: каталог от бека по заказу,
 * выбор сразу фиксируется в заказе (select), после чего страница перечитывает заказ — сумма к оплате
 * и расклад цены обновляются с бека. Показывается только до оплаты; без доступных услуг блок не рендерится.
 */
const OrderAdditionalOptionsComponent = ({ reloadOrder }) => {
  const { t, language } = i18next;
  const { orderData } = useSelector(state => state.order);
  const { order_id, session_id, options: orderOptions, passenger_data: passengers } = orderData || {};

  const [catalog, setCatalog] = useState(null); // null — ещё грузится
  const [selected, setSelected] = useState({}); // { "optionId:code": quantity }
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [openSeatMap, setOpenSeatMap] = useState(null); // "optionId|segmentRef" раскрытой схемы салона

  // выбранные ранее (ещё не оплаченные) — из заказа
  useEffect(() => {
    const current = {};

    (orderOptions || []).forEach(option => {
      if (option.status === "selected") {
        current[keyOf(option.additional_option_id, option.code, option.segment_ref ? option.passenger_index : null)] =
          option.quantity || 1;
      }
    });

    setSelected(current);
  }, [orderOptions]);

  const loadCatalog = () =>
    getAdditionalOptions({ order_id, session_id, lang: language })
      .then(response => {
        const options = (response?.data?.options || []).filter(option => (option.items || []).length > 0);
        setCatalog(options);
      })
      .catch(err => {
        console.log({ additionalOptionsError: err });
        setCatalog(current => current || []);
      });

  useEffect(() => {
    if (!order_id) return;

    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id, session_id, language]);

  if (!catalog || catalog.length === 0) return null;

  const save = next => {
    const options = Object.entries(next).map(([key, quantity]) => {
      const { optionId, code, passengerIndex } = parseKey(key);
      return {
        additional_option_id: optionId,
        code,
        quantity,
        ...(passengerIndex === null ? {} : { passenger_index: passengerIndex }),
      };
    });

    setIsSaving(true);
    setError(null);

    return selectAdditionalOptions({ order_id, session_id, options, lang: language })
      .then(response => {
        if (response?.data?.error_code) {
          setError(response.data.error_desc || t("additional_options_error"));
          // бек отказал (например, место только что заняли) — перечитываем каталог, чтобы схема салона была актуальной
          loadCatalog();
          return;
        }

        setSelected(next);
        if (reloadOrder) reloadOrder();
      })
      .catch(err => {
        console.log({ selectAdditionalOptionsError: err });
        setError(t("additional_options_error"));
      })
      .finally(() => setIsSaving(false));
  };

  const toggle = (option, item) => {
    const key = keyOf(option.additional_option_id, item.code);
    const next = { ...selected };

    if (next[key]) {
      delete next[key];
    } else {
      next[key] = 1;
    }

    save(next);
  };

  const changeQuantity = (option, item, delta) => {
    const key = keyOf(option.additional_option_id, item.code);
    const quantity = Math.min(item.max_quantity || 1, Math.max(1, (selected[key] || 1) + delta));

    if (quantity === selected[key]) return;

    save({ ...selected, [key]: quantity });
  };

  // Услуга перевозчика пассажиру: на одном плече у пассажира один вариант (20 / 30 / 40 кг, одно блюдо) —
  // выбор другого заменяет прежний, повторный клик по выбранному снимает его.
  const togglePassengerItem = (option, item, passengerIndex) => {
    const key = keyOf(option.additional_option_id, item.code, passengerIndex);
    const next = { ...selected };
    const wasSelected = !!next[key];

    option.items
      .filter(other => isPassengerItem(other) && other.segment_ref === item.segment_ref)
      .forEach(other => delete next[keyOf(option.additional_option_id, other.code, passengerIndex)]);

    if (!wasSelected) next[key] = 1;

    save(next);
  };

  // Питание: блюдо, которое уже есть у пассажира на этом плече. Название — из любого варианта опции
  // с таким кодом на том же плече (сам пассажир в нём не числится), иначе показываем код.
  const currentChoiceOf = (option, segmentRef, passengerIndex) => {
    const sameSegment = option.items.filter(item => item.segment_ref === segmentRef);
    const code = sameSegment.map(item => currentCodeOf(item, passengerIndex)).find(Boolean);
    if (!code) return null;

    const match = sameSegment.find(item => item.code === code || baseCode(item.code) === baseCode(code));
    return (match && itemName(match.name, language)) || code;
  };

  const passengerLabel = index => {
    const passenger = (passengers || [])[index];
    const name = passenger ? [passenger.surname, passenger.name].filter(Boolean).join(" ") : "";
    return name || `${t("passenger")} ${index + 1}`;
  };

  // Плечи опции с услугами перевозчика: [{ segmentRef, segmentName, items, passengerIndexes }]
  const segmentsOf = option => {
    const segments = [];

    option.items
      .filter(item => isPassengerItem(item) && !isSeatItem(item))
      .forEach(item => {
        let segment = segments.find(entry => entry.segmentRef === item.segment_ref);

        if (!segment) {
          segment = { segmentRef: item.segment_ref, segmentName: item.segment_name, items: [], passengerIndexes: [] };
          segments.push(segment);
        }

        segment.items.push(item);
        item.passenger_indexes.forEach(index => {
          if (!segment.passengerIndexes.includes(index)) segment.passengerIndexes.push(index);
        });
      });

    segments.forEach(segment => segment.passengerIndexes.sort((a, b) => a - b));

    return segments;
  };

  // Рейсы опции с выбором мест: [{ segmentRef, segmentName, items, passengerIndexes, seatsByPassenger }]
  const seatFlightsOf = option => {
    const flights = [];

    option.items.filter(isSeatItem).forEach(item => {
      let flight = flights.find(entry => entry.segmentRef === item.segment_ref);

      if (!flight) {
        flight = {
          segmentRef: item.segment_ref,
          segmentName: item.segment_name,
          items: [],
          passengerIndexes: [],
          seatsByPassenger: {},
        };
        flights.push(flight);
      }

      flight.items.push(item);
      (item.passenger_indexes || []).forEach(index => {
        if (!flight.passengerIndexes.includes(index)) flight.passengerIndexes.push(index);
        if (selected[keyOf(option.additional_option_id, item.code, index)]) flight.seatsByPassenger[index] = item.code;
      });
    });

    flights.forEach(flight => flight.passengerIndexes.sort((a, b) => a - b));

    return flights;
  };

  // Место пассажиру на рейсе: прежнее место этого пассажира на рейсе снимается; item = null — просто снять
  const pickSeat = (option, flight, passengerIndex, item) => {
    const next = { ...selected };

    flight.items.forEach(other => delete next[keyOf(option.additional_option_id, other.code, passengerIndex)]);
    if (item) next[keyOf(option.additional_option_id, item.code, passengerIndex)] = 1;

    save(next);
  };

  return (
    <Wrapper>
      <Title>{t("additional_options")}</Title>
      <Hint>{t("additional_options_hint")}</Hint>

      {catalog.map(option => (
        <div key={option.additional_option_id}>
          <OptionName>{option.name}</OptionName>

          {segmentsOf(option).map(segment => (
            <div key={segment.segmentRef}>
              <SegmentTitle>{segment.segmentName}</SegmentTitle>

              {segment.passengerIndexes.map(passengerIndex => {
                const currentChoice = currentChoiceOf(option, segment.segmentRef, passengerIndex);

                return (
                  <div key={passengerIndex}>
                    <PassengerName>{passengerLabel(passengerIndex)}</PassengerName>
                    {currentChoice ? (
                      <CurrentChoice>{t("meal_current_replace", { meal: currentChoice })}</CurrentChoice>
                    ) : null}
                    <Chips>
                      {segment.items
                        .filter(item => item.passenger_indexes.includes(passengerIndex))
                        .map(item => {
                          const isSelected = !!selected[keyOf(option.additional_option_id, item.code, passengerIndex)];
                          const hasPrice = item.price !== null && item.price !== undefined;

                          return (
                            <Chip
                              key={item.code}
                              type="button"
                              $isSelected={isSelected}
                              disabled={isSaving || !hasPrice}
                              onClick={() => togglePassengerItem(option, item, passengerIndex)}
                            >
                              <span className="chip__name">{itemName(item.name, language)}</span>
                              <span className="chip__price">
                                {hasPrice
                                  ? `${isSelected ? "✓ " : "+ "}${normalizePrice(item.price)} ${item.currency}`
                                  : t("price_on_request")}
                              </span>
                            </Chip>
                          );
                        })}
                    </Chips>
                  </div>
                );
              })}
            </div>
          ))}

          {seatFlightsOf(option).map(flight => {
            const mapKey = `${option.additional_option_id}|${flight.segmentRef}`;
            const isOpen = openSeatMap === mapKey;
            const chosen = flight.passengerIndexes.filter(index => flight.seatsByPassenger[index]);
            const chosenTotal = chosen.reduce((sum, index) => {
              const item = flight.items.find(entry => entry.code === flight.seatsByPassenger[index]);
              return sum + (item?.price || 0);
            }, 0);

            return (
              <div key={flight.segmentRef}>
                <SegmentTitle>{flight.segmentName}</SegmentTitle>
                <SeatSummary>
                  <span>
                    {chosen.length > 0
                      ? `${chosen.map(index => `${passengerLabel(index)} — ${flight.seatsByPassenger[index].split("@")[0]}`).join(", ")}${
                          chosenTotal > 0 ? ` · ${normalizePrice(chosenTotal)} ${flight.items[0]?.currency || ""}` : ""
                        }`
                      : t("seats_not_selected")}
                  </span>
                  <SeatToggle type="button" onClick={() => setOpenSeatMap(isOpen ? null : mapKey)}>
                    {isOpen ? t("seats_hide_map") : t("seats_choose")}
                  </SeatToggle>
                </SeatSummary>

                {isOpen ? (
                  <SeatMap
                    items={flight.items}
                    passengerIndexes={flight.passengerIndexes}
                    passengerLabel={passengerLabel}
                    seatsByPassenger={flight.seatsByPassenger}
                    onPick={(passengerIndex, item) => pickSeat(option, flight, passengerIndex, item)}
                    disabled={isSaving}
                  />
                ) : null}
              </div>
            );
          })}

          {option.items
            .filter(item => !isPassengerItem(item))
            .map(item => {
              const key = keyOf(option.additional_option_id, item.code);
              const quantity = selected[key] || 0;
              const isSelected = quantity > 0;
              const hasPrice = item.price !== null && item.price !== undefined;
              const total = hasPrice ? item.price * (quantity || 1) : null;

              return (
                <Item key={key} $isSelected={isSelected} style={{ marginTop: 8 }}>
                  <ItemInfo>
                    <span className="item__name">{itemName(item.name, language)}</span>
                    {item.description ? <span className="item__description">{item.description}</span> : null}
                    <span className="item__price">
                      {hasPrice ? (
                        <>
                          {normalizePrice(total)} {item.currency}
                          {quantity > 1 ? ` (${normalizePrice(item.price)} × ${quantity})` : ""}
                        </>
                      ) : (
                        t("price_on_request")
                      )}
                    </span>
                  </ItemInfo>

                  <ItemControls>
                    {isSelected && (item.max_quantity || 1) > 1 ? (
                      <>
                        <QuantityButton
                          type="button"
                          disabled={isSaving || quantity <= 1}
                          onClick={() => changeQuantity(option, item, -1)}
                        >
                          −
                        </QuantityButton>
                        <Quantity>{quantity}</Quantity>
                        <QuantityButton
                          type="button"
                          disabled={isSaving || quantity >= (item.max_quantity || 1)}
                          onClick={() => changeQuantity(option, item, 1)}
                        >
                          +
                        </QuantityButton>
                      </>
                    ) : null}

                    <ToggleButton
                      type="button"
                      $isSelected={isSelected}
                      disabled={isSaving || !hasPrice}
                      onClick={() => toggle(option, item)}
                    >
                      {isSaving ? (
                        <DotsLoadingComponent color={isSelected ? "#E63946" : "#fff"} />
                      ) : isSelected ? (
                        t("remove_option")
                      ) : (
                        t("add_option")
                      )}
                    </ToggleButton>
                  </ItemControls>
                </Item>
              );
            })}
        </div>
      ))}

      {error ? <ErrorText>{error}</ErrorText> : null}
    </Wrapper>
  );
};

export default OrderAdditionalOptionsComponent;
